import { useEffect } from 'react';

import { StoryBoardTracks } from 'services/storyboard.service';
import { useAppSelector } from 'store/store-hooks';
import { useResizeObserver } from 'hooks/use-resize-observer';
import { useLocation } from 'components/_hooks/use-router';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { TrackRoot, TrackFeed, useZoomContext } from './lib/zoom.provider';
import { useZoomScroll } from './lib/use-zoom-scroll';
import { useZoomTouch } from './lib/use-zoom-touch';

const ERR_ID = 'use-zoom-effects.ts';

type Ref<T> = React.RefObject<T>;

export const useZoomWs = (root: HTMLDivElement) => {
  const waveIsReady = useAppSelector((state) => state.audio.waveIsReady);
  const configPanel = useAppSelector((state) => state.editor.configPanel);
  const { wsRoot, wsWave } = useAudioTrackContext();
  const { onWheel, onResize, onResizeObserver, onWaveReady, handleSideBar } = useZoomContext();
  const { onScroll } = useZoomScroll();
  const { path } = useLocation();
  const onTouch = useZoomTouch();

  useResizeObserver(wsRoot.current ?? null, onResizeObserver);

  useEffect(() => {
    if (!root) {
      console.error(`[${ERR_ID}] useAudioZoom: useEffect[]`);
      return;
    }
    wsRoot.current = root;
    wsWave.current = root.querySelector('wave');
    const element = wsRoot.current;

    const touch = onTouch(wsWave);
    const onWheelListener = (e: WheelEvent) => onWheel({ event: e, scrollElement: null, zoomElementType: 'ws' });

    element.addEventListener('pointerdown', touch.start, { passive: false });
    element.addEventListener('pointermove', touch.move, { passive: false });
    element.addEventListener('pointerup', touch.end, { passive: false });
    element.addEventListener('touchmove', (e) => e.preventDefault());
    element.addEventListener('wheel', onWheelListener);

    return () => {
      element.removeEventListener('pointerdown', touch.start);
      element.removeEventListener('pointermove', touch.move);
      element.removeEventListener('pointerup', touch.end);
      element.removeEventListener('wheel', onWheelListener);
    };
  }, [path]);

  useEffect(() => {
    if (!waveIsReady) return;
    if (!wsWave.current) {
      console.error(`[${ERR_ID}] useAudioZoom: useEffect[waveIsReady]`);
      return;
    }
    const onScrollListener = (e: Event) => onScroll(e);
    const track = wsWave.current;
    track.addEventListener('scroll', onScrollListener);
    return () => {
      track.removeEventListener('scroll', onScrollListener);
    };
  }, [waveIsReady]);

  useEffect(() => {
    if (!waveIsReady) return;
    onWaveReady();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [waveIsReady]);

  useEffect(() => {
    return () => handleSideBar();
  }, [configPanel]);
};

export const useZoomSb = (trackRoot: Ref<TrackRoot>, trackFeed: Ref<TrackFeed>, trackId: StoryBoardTracks) => {
  const waveIsReady = useAppSelector((state) => state.audio.waveIsReady);
  const scrollSyncLock = useAppSelector((state) => state.sb.scrollSyncLock);
  const sbTrack = useAppSelector((state) => state.editor__controls.sbPosition.track);
  const { trackElements, elementsToScroll, elementsToZoom, onWheel, handleSideBar, handleScroll } = useZoomContext();
  const { onScroll } = useZoomScroll();
  const { wsWave } = useAudioTrackContext();

  useEffect(() => {
    if (!waveIsReady) return;
    if (!trackRoot.current || !trackFeed.current) {
      console.error(`[${ERR_ID}] useStoryboardTrack: useEffect[]`);
      return;
    }
    trackElements.current.push([trackRoot.current, trackFeed.current]);
    elementsToZoom.current.push(trackFeed.current);
    handleSideBar();

    return () => {
      trackElements.current = [];
      elementsToZoom.current = [];
      elementsToScroll.current = [];
    };
  }, [waveIsReady]);

  useEffect(() => {
    if (!waveIsReady) return;

    const track = trackRoot.current!;
    const isSelected = sbTrack === trackId;

    if (scrollSyncLock) {
      elementsToScroll.current.push(track);
      handleScroll(wsWave.current!.scrollLeft);
    } else if (isSelected) {
      elementsToScroll.current = [track];
      handleScroll(track.scrollLeft);
    }

    const onWheelListener = (e: WheelEvent) => {
      onWheel({
        event: e,
        scrollElement: scrollSyncLock ? null : isSelected ? null : track,
        zoomElementType: trackId,
      });
    };
    const onScrollListener = (e: Event) => onScroll(e);

    track.addEventListener('wheel', onWheelListener);
    if (scrollSyncLock) track.addEventListener('scroll', onScrollListener);
    else if (isSelected) track.addEventListener('scroll', onScrollListener);

    return () => {
      track.removeEventListener('wheel', onWheelListener);
      track.removeEventListener('scroll', onScrollListener);
      elementsToScroll.current = [];
    };
  }, [waveIsReady, sbTrack, scrollSyncLock]);
};

export const useZoomFc = (trackRoot: Ref<TrackRoot>, trackFeed: Ref<TrackFeed>) => {
  const waveIsReady = useAppSelector((state) => state.audio.waveIsReady);
  const { trackElements, elementsToScroll, elementsToZoom, onWheel } = useZoomContext();
  const { onScroll } = useZoomScroll();

  useEffect(() => {
    if (!waveIsReady) return;
    if (!trackRoot.current || !trackFeed.current) {
      console.error(`[${ERR_ID}] useZoomFc: useEffect`);
      return;
    }
    trackElements.current = [[trackRoot.current, trackFeed.current]];
    elementsToScroll.current = [trackRoot.current];
    elementsToZoom.current = [trackFeed.current];

    const track = trackRoot.current;

    const onWheelListener = (e: WheelEvent) =>
      onWheel({
        event: e,
        scrollElement: null,
        zoomElementType: 'finalcuts',
      });
    const onScrollListener = (e: Event) => onScroll(e);

    track.addEventListener('wheel', onWheelListener);
    track.addEventListener('scroll', onScrollListener);

    return () => {
      if (!waveIsReady) return;
      trackElements.current = [];
      elementsToScroll.current = [];
      elementsToZoom.current = [];
      track.removeEventListener('wheel', onWheelListener);
      track.removeEventListener('scroll', onScrollListener);
    };
  }, [waveIsReady]);
};
